import { Visibility, VisibilityOutlined } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { LoginBanner } from "asset";
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import PhoneInput from "react-phone-number-input";
import { useFormik } from "formik";
import {
  deleteUser,
  EmailAuthProvider,
  linkWithCredential,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updateProfile,
} from "firebase/auth";
import { auth, database } from "configs/firebaseConfigs";
import Swal from "sweetalert2";
import { set, ref } from "firebase/database";
import useDataFetch from "hooks/useDataFetch";
import { db, useIsMounted } from "hooks";

const Auth = ({ children, header, link }) => {
  return (
    <main
      style={{
        minHeight: "100vh",
      }}
      className="auth-up-main flex-row align-items-center "
    >
      <Container>
        <section className="auth-up-main-section">
          <Grid container spacing={2}>
            <Grid item xs={0} md={6}>
              <div className="auth-up-left-content flex-row align-items-center justify-content-center ">
                <img src={LoginBanner} alt="" />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="auth-right-page-content flex-column gap-2">
                <div className="auth-up-top-header w-100 flex-row align-items-center gap-2 ">
                  <p className="auth-up-header-p">{header.tagline}</p>
                  <span className="auth-up-header-btn">
                    <NavLink to={link}>
                      <Button>{header.btn}</Button>
                    </NavLink>
                  </span>
                </div>

                <div className="auth-up-title w-100 flex-column gap-1 ">
                  <h3 className="auth-up-title-h3">{header.title}</h3>
                  <p className="auth-up-title-p">{header.titleTag}</p>
                </div>

                <div className="auth-form-field flex-column gap-2">
                  {children}
                </div>
              </div>
            </Grid>
          </Grid>
        </section>
      </Container>
    </main>
  );
};

const Signup = () => {
  const [visiblePassword, setVisiblePassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [phoneValue, setPhoneValue] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [agreeTerm, setAgreeTerm] = useState(true);
  const [showOTP, setShowOTP] = useState(false);
  const [referredUserId, setReferredUserId] = useState("");

  const [otpLoading, setOtpLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [otpGenerated, setOtpGenerated] = useState(false);

  const [searchParams] = useSearchParams();

  const isMounted = useIsMounted();

  const navigate = useNavigate();

  const referralCodeString = searchParams.get("referralCode");

  let allUser = useDataFetch("/users");

  let validationSchema = yup.object({
    name: yup.string().required("Name is required").min(3).max(50),
    email: yup.string().required("Email is required").email("Invalid email"),
    phone: yup
      .string()
      .required("Phone is required")
      .min(10, "At least 10 digits")
      .matches(
        /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s./0-9]*$/g,
        "Phone number is invalid"
      ),
    otp: yup.string().required("OTP is required"),
    referralCode: yup.string().nullable(),
    password: yup
      .string()
      .required("Password is required")
      .min(6, "At least 6 characters"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      otp: "",
      referralCode: referralCodeString || "",
      password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, submitProps) => {
      try {
        if (!agreeTerm) {
          Swal.fire({
            icon: "warning",
            text: "Please agree to our terms, conditions and privacy policy",
          });
          return;
        }
        let userExist = allUser?.data?.some(
          (user) => user?.email === values?.email
        );
        if (userExist) {
          Swal.fire({
            icon: "warning",
            text: "User already exist",
          });
          return;
        }

        setSignUpLoading(true);
        const result = await confirmResult?.confirm(values?.otp);
        // User signed in successfully.
        if (!result) {
          Swal.fire({
            icon: "error",
            title: "",
            text: "OTP provided is wrong!",
          });
          return;
        }
        // const user = result.user;

        const credential = EmailAuthProvider.credential(
          values?.email,
          values?.password
        );
        //link with email credential from email login
        linkWithCredential(result?.user, credential)
          .then((userCred) => {
            const user = userCred.user;
            updateProfile(user, {
              displayName: formik?.values?.name,
            })
              .then(async () => {
                //generate a random referral code

                const referralCode =
                  Math.random()
                    .toString(36)
                    .replace(/[^a-z]+/g, "")
                    .substr(0, 4)
                    .toUpperCase() + user?.phoneNumber.split("+")[1];

                //create user to database with user id
                const userData = {
                  displayName: formik?.values?.name,
                  uid: user?.uid,
                  createdAt: user?.metadata?.createdAt,
                  lastSignIn: user?.metadata?.lastLoginAt,
                  phoneNumber: user?.phoneNumber,
                  emailVerified: user?.emailVerified || false,
                  role: "user",
                  dateOfBirth: "",
                  email: user?.email,
                  isBlocked: false,
                  photoURL: user?.photoURL,
                  referralCode: referralCode,
                  referredBy: referredUserId,
                  referred: [],
                  updatedAt: user?.metadata?.lastLoginAt,
                  address: [],
                };

                //update referred user with user id

                if (referredUserId) {
                  await db.pushData(
                    `/users/${referredUserId}/referred`,
                    userData.uid
                  );
                }

                await set(ref(database, "users/" + user?.uid), {
                  ...userData,
                });

                Swal.fire({
                  icon: "success",
                  title: "Account Creation Successful",
                }).then(() => {
                  navigate("/profile");
                });
              })
              .catch(async (error) => {
                console.log(error);
                setSignUpLoading(false);
                await deleteUser(result?.user);
                Swal.fire({
                  icon: "error",
                  title: "Account Creation Failed",
                });
              });
          })
          .catch(async (error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "",
              text: "Something went wrong, please try again",
            });
          });
        setSignUpLoading(false);
        submitProps.resetForm();
        setPhoneValue("");
      } catch (error) {
        console.log(error);
        Swal.fire({
          icon: "error",
          text: "Something went wrong!",
        });
        setSignUpLoading(false);
        submitProps.resetForm();
        setPhoneValue("");
      }
    },
  });

  // console.log(phoneValue);

  // console.log(formik.errors);

  // console.log(loadingBtn);

  //generate OTP
  const generateOtp = () => {
    // console.log("otp");
    // console.log(phoneValue);
    try {
      if (otpGenerated) {
        Swal.fire({
          icon: "warning",

          title: "OTP already generated",
          text: "Please wait or refresh the page and try again",
        });
        return;
      }
      setOtpLoading(true);
      let userExist = allUser?.data?.some(
        (user) => user?.phoneNumber === phoneValue
      );
      if (userExist) {
        Swal.fire({
          icon: "error",
          title: "Phone number  already exist!",
          text: "Please login instead",
        });
        setOtpLoading(false);
        return;
      }
      setOtpGenerated(true);

      window.reCaptchaVerifier = new RecaptchaVerifier(
        "re-captcha-verifier",
        {
          size: "invisible",
          callback: (response) => {},
        },
        auth
      );

      let phoneNumber = phoneValue;
      signInWithPhoneNumber(auth, phoneNumber, window.reCaptchaVerifier)
        .then((confirmationResult) => {
          Swal.fire({
            title: "Message sent",
            text: "Verify your OTP below ",
            icon: "success",
            confirmButtonText: "OK",
          });
          setConfirmResult(confirmationResult);
          setShowOTP(true);
          setOtpLoading(false);
          // window.reCaptchaVerifier.reset();
        })
        .catch((error) => {
          Swal.fire({
            title: "OOPs! Something went wrong ",
            text: "",
            icon: "error",
            confirmButtonText: "Try Again",
          });
          console.log(error);
          setOtpLoading(false);
        });
    } catch (error) {
      setOtpLoading(false);
      console.log(error);
      Swal.fire({
        title: "OOPs! Something went wrong ",
        text: "",
        icon: "error",
        confirmButtonText: "Try Again",
      });
    }
  };

  console.log(otpLoading);

  useEffect(() => {
    if (isMounted.current) {
      const checkReferralCode = async () => {
        try {
          const referralCode = formik?.values?.referralCode;

          const referralTrue = allUser?.data?.find((user) => {
            return user?.referralCode === referralCode;
          });
          if (!referralTrue) {
            formik.setFieldError("referralCode", "Referral code is not valid");
            setReferredUserId("");
          } else {
            setReferredUserId(referralTrue?.uid);
          }
        } catch (error) {
          console.log(error);
          window.location.reload();
        }
      };
      if (formik?.values?.referralCode) {
        checkReferralCode();
      }
      return;
    }
  }, [formik?.values?.referralCode, isMounted, allUser?.data]);

  return (
    <Auth
      header={{
        tagline: "Already have an account  ",
        btn: "Sign in",
        title: "Welcome to water",
        titleTag: "Register your account",
        actionBtn: "Register",
        socialTag: "Create account with",
      }}
      link={"/login"}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="sign-up-name auth-input-form">
          <InputLabel> Name </InputLabel>

          <TextField
            className="w-100"
            name="name"
            type={"text"}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && formik.errors.name ? true : false}
            helperText={formik.touched.name && formik.errors.name}
            placeholder="Enter your full name"
          />
        </div>
        <div className="sign-up-email auth-input-form">
          <InputLabel> Email </InputLabel>

          <TextField
            className="w-100"
            name="email"
            type={"email"}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email ? true : false}
            helperText={formik.touched.email && formik.errors.email}
            placeholder="Enter your email"
          />
        </div>
        <div className="sign-up-phone auth-input-form">
          <InputLabel> Phone </InputLabel>

          <PhoneInput
            international
            countryCallingCodeEditable={false}
            className="w-100 country-phone-input flex-row align-items-center "
            placeholder="Enter phone number"
            value={phoneValue}
            disabled={showOTP}
            onChange={(value) => {
              setPhoneValue(value);
              formik.setFieldValue("phone", value);
            }}
            onBlur={(value) => {
              formik.handleBlur("phone");
            }}
            defaultCountry="IN"
          />
          <h1
            style={{
              width: "100%",
              fontSize: "12px",
              color: "#d32f2f",
              paddingTop: "0.5rem",
              fontWeight: "400",
            }}
          >
            {formik.touched.phone && formik.errors.phone}
          </h1>

          {phoneValue !== "" && (
            <span className="sign-up-otp">
              <Button type="button" onClick={generateOtp}>
                {otpLoading ? (
                  <CircularProgress
                    sx={{
                      color: "white",
                      width: "2rem !important",
                      height: "2rem !important",
                    }}
                  />
                ) : (
                  "Generate OTP"
                )}
              </Button>
              {showOTP && (
                <TextField
                  className="w-100"
                  name="otp"
                  type={"text"}
                  value={formik.values.otp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.otp && formik.errors.otp ? true : false}
                  helperText={formik.touched.otp && formik.errors.otp}
                  placeholder="Enter OTP"
                />
              )}
            </span>
          )}
        </div>
        <div
          aria-label="referral-code"
          className="sign-up-password auth-input-form"
        >
          <InputLabel> Referral Code(Optional) </InputLabel>

          <TextField
            className="w-100"
            placeholder="Enter Referral Code if any"
            type={"text"}
            name="referralCode"
            value={formik.values.referralCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.referralCode && formik.errors.referralCode
                ? true
                : false
            }
            helperText={
              formik.touched.referralCode && formik.errors.referralCode
            }
          />
        </div>
        <div
          aria-label="auth-password"
          className="sign-up-password auth-input-form"
        >
          <InputLabel> Password </InputLabel>

          <span className="auth-pass flex-row align-items-center">
            <TextField
              className="w-100"
              placeholder="Enter password"
              type={visiblePassword?.password ? "text" : "password"}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.password && formik.errors.password ? true : false
              }
              helperText={formik.touched.password && formik.errors.password}
            />
            <span
              className="flex-row align-items-center justify-content-center password-eye-icon"
              onClick={() => {
                setVisiblePassword((prev) => {
                  return {
                    ...prev,
                    password: !prev.password,
                  };
                });
              }}
            >
              {visiblePassword?.password ? (
                <Visibility />
              ) : (
                <VisibilityOutlined />
              )}
            </span>
          </span>
        </div>
        <div
          aria-label="auth-password"
          className="sign-up-password auth-input-form"
        >
          <InputLabel>Confirm Password </InputLabel>

          <span className="auth-pass flex-row align-items-center">
            <TextField
              className="w-100"
              placeholder="Confirm password"
              type={visiblePassword?.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword && formik.errors.confirmPassword
                  ? true
                  : false
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
            />
            <span
              className="flex-row align-items-center justify-content-center password-eye-icon"
              onClick={() => {
                setVisiblePassword((prev) => {
                  return {
                    ...prev,
                    confirmPassword: !prev.confirmPassword,
                  };
                });
              }}
            >
              {visiblePassword?.confirmPassword ? (
                <Visibility />
              ) : (
                <VisibilityOutlined />
              )}
            </span>
          </span>
        </div>
        <div className="auth-terms-use flex-row gap-1 align-items-center ">
          <Checkbox
            checked={agreeTerm}
            onChange={(value) => {
              setAgreeTerm(value.target.checked);
            }}
            inputProps={{ "aria-label": "controlled" }}
          />{" "}
          <span className="flex-row align-items-center gap-1 flex-wrap">
            I agree to all the{" "}
            <span className="auth-term flex-row align-items-center">Terms</span>{" "}
            and{" "}
            <span className="auth-term flex-row align-items-center flex-wrap">
              {" "}
              Privacy policy.{" "}
            </span>{" "}
          </span>
        </div>
        <div className="auth-btn flex-row gap-2 ">
          <Button type="submit">
            {signUpLoading ? (
              <CircularProgress
                sx={{
                  color: "white",
                  width: "2rem !important",
                  height: "2rem !important",
                }}
              />
            ) : (
              "Sign Up"
            )}
          </Button>
        </div>

        <div id="re-captcha-verifier"></div>
      </form>
    </Auth>
  );
};

export default Signup;

export { Auth };
